/**
 * Tool Print & PDF Export button (WEB-121 — R1.4 sub-step 4).
 *
 * Hits the browser's native `window.print()`. The user picks "Save as
 * PDF" from the print dialog to get a PDF; the `@media print` rules in
 * `apps/web/src/styles/index.css` strip nav / submit buttons / tabs and
 * expand textareas so the printed page is a clean tool snapshot.
 *
 * The button itself carries `data-print-hide="true"` so it hides itself
 * on print (covered by the @media print rule).
 */

import { useTranslation } from 'react-i18next';

interface PrintButtonProps {
  /** Optional override for the displayed label. */
  label?: string;
  /** Optional callback invoked just before `window.print()`. */
  onBeforePrint?: () => void;
  className?: string;
}

export function PrintButton({
  label,
  onBeforePrint,
  className = '',
}: PrintButtonProps): JSX.Element {
  const { t } = useTranslation();
  const text = label ?? t('tools.common.printPdf');
  return (
    <button
      type="button"
      data-print-hide="true"
      data-testid="print-button"
      onClick={() => {
        onBeforePrint?.();
        window.print();
      }}
      className={[
        'btn-ghost text-sm border border-bone-3 hover:border-flame',
        'inline-flex items-center gap-s2',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      aria-label={text}
    >
      <span aria-hidden="true">⎙</span>
      {text}
    </button>
  );
}
